import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import { Link } from 'react-router-dom';
import Moment from 'moment';

import { withRouter } from '../../utils/withRouter';
import { updateLegacySessionKey, updateScript } from '../../store/actions';
import { pullCampaignScripts, updateOrCreateScript } from '../../store/api/calls';
import api from '../../store/api/api';

import * as formFields from '../../utils/forms/formFields';
import { siteCanvassEditCreateForm } from '../../utils/forms/formFields';

import NavBaseTemplate from '../templates/NavBaseTemplate';
import BaseForm from '../theme/forms/BaseForm';
import DataTable from '../theme/tables/DataTable';
import BaseModal from '../theme/modals/BaseModal';
import FeedbackTile from '../theme/modals/FeedbackTile';
import ScriptEditForm from '../theme/scripts/ScriptEditForm';

class ScriptScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editModalOpen: false,
      deleteModalOpen: false,
      publishModalOpen: false,
      scriptCreateModelOpen: false,
      publishSuccess: false,
      showInactive: false,
      loading: false,
      error: false,
      editingInstance: {},
    };

    this.formInputs = siteCanvassEditCreateForm;
  }

  componentDidMount() {
    const legacyCookie = this.props.cookies.get('sessionid_wox_legacy');
    this.props.updateLegacySessionKey(legacyCookie);
    this.handleMountOrCampaignUpdate();
  }

  componentDidUpdate(prevProps) {
    if (this.props.focusedCampaign !== prevProps.focusedCampaign) {
      this.handleMountOrCampaignUpdate();
    }
  }

  pullScripts = () => {
    const params = this.state.showInactive ? { deactivated: 1 } : {};
    return pullCampaignScripts(params);
  };

  handleMountOrCampaignUpdate = () => {
    this.pullScripts();
  };

  toggleShowInactive = () => {
    this.setState({ showInactive: !this.state.showInactive }, this.pullScripts);
  };

  /* -----------------------------
   * Create Script
   * ----------------------------- */

  launchCreateScriptModal = () => {
    this.setState({ scriptCreateModelOpen: true });
  };

  onCloseCreateScriptModal = () => {
    this.setState({ scriptCreateModelOpen: false });
  };

  handleCreateScriptRequest = (data) => {
    const { name } = data;
    api.get('/script/new/', { script_name: name }).then((res) => {
      this.props.history.push(`/script/${res.data.script_id}/edit`);
    });
  };

  /* -----------------------------
   * Edit Script (name + is_active)
   * ----------------------------- */

  onOpenEditModal = (modelInstance = {}) => {
    this.setState({
      editModalOpen: true,
      editingInstance: modelInstance,
    });
  };

  onCloseEditModal = () => {
    this.setState({
      editModalOpen: false,
      editingInstance: {},
    });
  };

  /* -----------------------------
   * Publish Script
   * ----------------------------- */

  onOpenPublishModal = (modelInstance) => {
    this.setState({
      publishModalOpen: true,
      editingInstance: modelInstance,
    });
  };

  onClosePublishModal = () => {
    this.setState({
      publishModalOpen: false,
      editingInstance: {},
    });
  };

  handlePublishScriptRequest = () => {
    const { id: script_id, draft_revision_num: revision_num } = this.state.editingInstance;

    this.setState({ loading: true });

    api.get('/script/publish/', { script_id, revision_num }).then((res) => {
      this.setState({
        loading: false,
        publishSuccess: true,
      });

      this.onClosePublishModal();
      setTimeout(() => this.setState({ publishSuccess: false }), 4000);

      // 🔑 Ensure table data updates
      this.pullScripts();
    });
  };

  /* -----------------------------
   * Delete Script
   * ----------------------------- */

  onOpenDeleteModal = (modelInstance = {}) => {
    this.setState({
      deleteModalOpen: true,
      editingInstance: modelInstance,
    });
  };

  onCloseDeleteModal = () => {
    this.setState({
      deleteModalOpen: false,
      editingInstance: {},
    });
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    updateOrCreateScript({ id, is_active: false }).then(this.onCloseDeleteModal);
  };

  /* -----------------------------
   * Table Data
   * ----------------------------- */

  _filterKeysForDataTable() {
    return this.props.scripts.map((d) => ({
      name: <Link to={`/script/${d.id}/edit`}>{d.name}</Link>,
      _name: d.name,

      latest_draft_revision: d.draft_revision_num,
      published_revision: d.pub_revision_num || '--',
      _published_revision: d.pub_revision_num || 0,

      created_on: new Moment(d.created_at).format('MM/DD/YY'),
      _created_on: new Moment(d.created_at),

      // RIGHT-MOST COLUMN — NO HEADER
      '\u200B': (() => {
        const hasUnpublishedChanges =
          !d.pub_revision_num || d.pub_revision_num !== d.draft_revision_num;

        return (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <button
              className="et-datatable-button button button-outline"
              onClick={() => this.onOpenEditModal(d)}
            >
              Edit
            </button>
            {hasUnpublishedChanges && (
              <button
                className="et-datatable-button button button-outline"
                onClick={() => this.onOpenPublishModal(d)}
              >
                Publish
              </button>
            )}
            <button
              className="et-datatable-button et-datatable-button-red button button-outline"
              onClick={() => this.onOpenDeleteModal(d)}
            >
              Remove
            </button>
          </div>
        );
      })(),

      _data: d,
    }));
  }

  /* -----------------------------
   * Render
   * ----------------------------- */

  render() {
    return (
      <NavBaseTemplate location={this.props.location}>
        <div className="button-table-top-wrapper">
          <a className="button" onClick={this.launchCreateScriptModal}>
            + Create Script
          </a>
          <a className="button button-outline" onClick={this.toggleShowInactive}>
            {this.state.showInactive ? 'Show Active' : 'Show Inactive'}
          </a>
        </div>

        <DataTable
          data={this._filterKeysForDataTable() || []}
          title={this.state.showInactive ? 'Inactive Scripts' : 'Scripts'}
        />

        <FeedbackTile
          text={
            <div>
              <i className="fas fa-asterisk fa-spin" style={{ marginRight: '15px' }} />
              Loading...
            </div>
          }
          visible={this.props.loading}
        />

        <FeedbackTile
          text={
            <div>
              <i className="fas fa-check-circle" style={{ marginRight: '15px' }} />
              Script Published
            </div>
          }
          visible={this.state.publishSuccess}
        />

        {/* CREATE SCRIPT MODAL */}
        <BaseModal
          open={this.state.scriptCreateModelOpen}
          onClose={this.onCloseCreateScriptModal}
          center
        >
          <BaseForm
            inputs={[formFields.genericName]}
            onCancel={this.onCloseCreateScriptModal}
            onSubmit={this.handleCreateScriptRequest}
          />
        </BaseModal>

        {/* EDIT SCRIPT MODAL — name + is_active */}
        <ScriptEditForm
          open={this.state.editModalOpen}
          script={this.state.editingInstance}
          onClose={this.onCloseEditModal}
          onSaved={this.pullScripts}
        />

        {/* PUBLISH CONFIRM MODAL */}
        <BaseModal open={this.state.publishModalOpen} onClose={this.onClosePublishModal} center>
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to publish the script <b>{this.state.editingInstance.name}</b>?
            </p>
            <div className="button-wrapper">
              <div className="button" onClick={this.handlePublishScriptRequest}>
                Publish
              </div>
              <div className="button button-outline" onClick={this.onClosePublishModal}>
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>

        {/* REMOVE — sets is_active false */}
        <BaseModal open={this.state.deleteModalOpen} onClose={this.onCloseDeleteModal} center>
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to remove the script <b>{this.state.editingInstance.name}</b>?
            </p>
            <div className="button-wrapper">
              <div className="button" onClick={this.handleRemoveSubmission}>
                Remove
              </div>
              <div className="button button-outline" onClick={this.onCloseDeleteModal}>
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </NavBaseTemplate>
    );
  }
}

/* -----------------------------
 * Redux
 * ----------------------------- */

const mapStateToProps = (state) => ({
  campaigns: state.campaigns?.data,
  focusedCampaign: state.session.focusedCampaign,
  scripts: state.scripts.data,
  loading: state.scripts.loading,
  error: state.scripts.error,
  is_admin: state.permissions.is_admin,
});

const mapDispatchToProps = (dispatch) => ({
  updateLegacySessionKey: (key) => dispatch(updateLegacySessionKey(key)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withCookies(withRouter(ScriptScreen)));
